import React, { useState } from 'react';
import {
    backgroundFlexibleProp,
    backgroundTypeProp,
    mediaQueryProp,
    videoProp,
} from 'utils/proptypes/modules/mediaProps';

import AspectWrapper from 'components/wrappers/AspectWrapper';
import FlexibleWrapper from 'components/wrappers/FlexibleWrapper';
import Heading from 'components/text/Heading';
import MaxWidthWrapper from 'components/wrappers/MaxWidthWrapper';
import PropTypes from 'prop-types';
import Video from 'components/base/video/Video';
import VideoPlayButton from 'components/buttons/styledButtons/VideoPlayButton';
import { above } from 'utils/mediaqueries';
import { headingTagProp } from 'utils/proptypes/modules/textProps';
import { mediaQueries } from 'config/theme/breakpoints';
import styled from 'libs/styled';

const Wrapper = styled('div')`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;

    video:focus {
        outline: none;
    }
`;

const Content = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const Tag = styled('span')`
    display: block;
    margin-bottom: 12px;
    font-size: 1.6rem;

    ${above.md} {
        margin-bottom: 24px;
        font-size: 1.8rem;
    }
`;

/**
 * A video block that will by displayed like a FlexibleBlock with a playbutton.
 * When the user presses the playbutton the video supplied to video-prop will be displayed instead.
 *
 * @param {object} background - A video/image object. This will be displayed before the play button is pressed.
 * @param {string} backgroundType - The type of background that should be used; video or image.
 * @param {string} heading - Heading as a string.
 * @param {string} headingTag - The html-tag used by heading; h1, h2, h3 etc.
 * @param {array} mediaQuery - Used if background is a image, can also be a part of the background object.
 * @param {string} tag - Small tag displayed below heading.
 * @param {object} video - A video object. This is the video that will start when the user presses play.
 */

const VideoPlayer = ({
    background,
    backgroundType,
    buttonText = '',
    heading = '',
    headingTag = 'h2',
    mediaQuery = mediaQueries.fullWidth,
    tag = '',
    video,
}) => {
    const { autoplay, controls, loop, muted, poster, url } = video;
    const [showVideo, setShowVideo] = useState(false);

    // When the user has pressed play
    return showVideo ? (
        <Wrapper>
            <MaxWidthWrapper includeContentMargins>
                {heading && (
                    <Heading as={headingTag} size="lg" mb={['32px', null, null, '56px']}>
                        {heading}
                    </Heading>
                )}
                {tag && <Tag>{tag}</Tag>}
            </MaxWidthWrapper>
            <AspectWrapper ratio={[3 / 4, 16 / 9]}>
                <Video
                    autoPlay={autoplay}
                    controls={controls}
                    loop={loop}
                    muted={muted}
                    playsInline={autoplay}
                    poster={poster && poster.url}
                    src={url}
                />
            </AspectWrapper>
        </Wrapper>
    ) : (
        // Before the user has pressed play
        <>
            <MaxWidthWrapper includeContentMargins>
                {heading && (
                    <Heading as={headingTag} size="lg" mb={['32px', null, null, '56px']}>
                        {heading}
                    </Heading>
                )}
                {tag && <Tag>{tag}</Tag>}
            </MaxWidthWrapper>
            <AspectWrapper ratio={[3 / 4, 16 / 9]}>
                <FlexibleWrapper background={background} backgroundType={backgroundType} mediaQuery={mediaQuery}>
                    <Content>
                        <VideoPlayButton onClick={() => setShowVideo(true)}>{buttonText}</VideoPlayButton>
                    </Content>
                </FlexibleWrapper>
            </AspectWrapper>
        </>
    );
};

VideoPlayer.propTypes = {
    background: backgroundFlexibleProp.isRequired,
    backgroundType: backgroundTypeProp.isRequired,
    buttonText: PropTypes.string,
    heading: PropTypes.string,
    headingTag: headingTagProp,
    mediaQuery: mediaQueryProp,
    tag: PropTypes.string,
    video: videoProp.isRequired,
};

export default VideoPlayer;
